/**
 * Consolidated retailer/customer sentiment reason taxonomy (15 canonical labels).
 *
 * Raw sources:
 * - great_data.sentiment_reason (voice — often full description sentences)
 * - great_digital_data.sentiment_reason (digital — often short canonical labels)
 *
 * Unmapped samples (2026-10 DB audit, n≈0 at top frequencies): extend KEYWORD_RULES if new
 * phrasing appears in QA pipeline output.
 */

/** Retailer sentiment QA copy starts this week (voice + digital cards). */
export const RETAILER_SENTIMENT_WEEK_START_LABEL = "week of 21 September 2026";

/** Voice return-error series uses `order_error` from this date (local midnight). */
export const VOICE_RETURN_ERRORS_MONITOR_START = new Date(2026, 8, 28);

export const formatVoiceReturnErrorsMonitorNote = () => {
  const d = VOICE_RETURN_ERRORS_MONITOR_START;
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "long" });
  const year = d.getFullYear();
  return `(Monitored from ${day} ${month} ${year})`;
};

/** Return-error series name shown on Average Score over time charts. */
export const VOICE_RETURN_ERRORS_SERIES_NAME = `Return Errors ${formatVoiceReturnErrorsMonitorNote()}`;

const isVoiceOrderErrorYes = (value) => {
  if (value === null || value === undefined) return false;
  const raw = String(value).trim().toLowerCase();
  if (!raw || raw === "n/a") return false;
  return raw === "yes" || raw === "y" || raw === "1" || raw === "true";
};

/**
 * Return errors: every row with order_error = Yes. The chart series label still
 * notes monitoring from 28 September 2026; that date does not hide earlier Yes rows.
 */
export const voiceRowCountsAsReturnError = (row) => isVoiceOrderErrorYes(row?.order_error);

/** @type {{ positive: { title: string, description: string }, neutral: { title: string, description: string }, negative: { title: string, description: string } }} */
export const SENTIMENT_BUCKET_DEFINITIONS = {
  positive: {
    title: "Happy",
    description:
      "The retailer sounds satisfied, appreciative, or cooperative — the interaction ended with their need met or a clear, acceptable path forward.",
  },
  neutral: {
    title: "Neutral",
    description:
      "Routine contact with no strong positive or negative signal — status checks, pending quotes, or clarification without clear frustration or delight.",
  },
  negative: {
    title: "Unhappy",
    description:
      "The retailer sounds frustrated, angry, or disappointed — delays, missed callbacks, repeated follow-ups, or no clear resolution.",
  },
};

/** @typedef {'positive' | 'neutral' | 'negative'} SentimentBucket */

/** Last-resort canonical reason when raw text is blank or unmatched (within bucket only). */
export const SENTIMENT_BUCKET_DEFAULT_REASON_ID = {
  positive: "happy-follow-up",
  neutral: "neutral-order-status",
  negative: "unhappy-no-resolution",
};

/** @type {{ id: string, bucket: SentimentBucket, label: string, description: string }[]} */
export const SENTIMENT_REASON_TAXONOMY = [
  {
    id: "happy-pricing",
    bucket: "positive",
    label: "Pricing / Quote Provided",
    description:
      "Retailer received the price, quote, pricing clarification or pricing update they needed.",
  },
  {
    id: "happy-order-complete",
    bucket: "positive",
    label: "Order / Request Successfully Completed",
    description:
      "Retailer's order, cancellation, return, RA, address change, replacement or other request was successfully processed.",
  },
  {
    id: "happy-ship-info",
    bucket: "positive",
    label: "Ship Date / Delivery Information Provided",
    description:
      "Retailer received a clear ship date, delivery timing, tracking or rush-shipping information.",
  },
  {
    id: "happy-product-details",
    bucket: "positive",
    label: "Product / Stock / Order Details Clarified",
    description:
      "Retailer received the product, stock, sizing, customization, specification or order information needed to proceed.",
  },
  {
    id: "happy-follow-up",
    bucket: "positive",
    label: "Clear Follow-up & Next Steps",
    description:
      "CSR provided a clear callback, email, update or next-step plan, giving the retailer confidence about what would happen next.",
  },
  {
    id: "neutral-order-status",
    bucket: "neutral",
    label: "Order Status / Missing Order Confirmation",
    description:
      "Retailer contacted customer service to check an order status, locate an order or confirm whether a reorder or order confirmation had been received.",
  },
  {
    id: "neutral-pricing-pending",
    bucket: "neutral",
    label: "Pricing / Quote Confirmation Pending",
    description:
      "Retailer was waiting for pricing, a quote, pricing correction or confirmation of a pricing discrepancy.",
  },
  {
    id: "neutral-delivery-pending",
    bucket: "neutral",
    label: "Delivery / Timing Confirmation Pending",
    description:
      "Retailer was uncertain about the ship date, delivery date, lead time or ability to meet a required date.",
  },
  {
    id: "neutral-product-clarify",
    bucket: "neutral",
    label: "Product / Sizing / Order Information Clarification",
    description:
      "Retailer needed additional clarification on sizing, product details, order information or another specification before proceeding.",
  },
  {
    id: "neutral-callback-pending",
    bucket: "neutral",
    label: "Callback / Investigation / Internal Confirmation Pending",
    description:
      "Retailer was waiting for another team or person to confirm, investigate or provide the next update.",
  },
  {
    id: "unhappy-order-delay",
    bucket: "negative",
    label: "Order Delayed Beyond Expected Date",
    description:
      "Retailer was unhappy because an order, project or requested work was taking significantly longer than expected, or a required deadline could not be met.",
  },
  {
    id: "unhappy-incorrect-info",
    bucket: "negative",
    label: "Incorrect/incomplete information",
    description:
      "The information you gave me is not sufficient or correct, so I cannot move forward.",
  },
  {
    id: "unhappy-no-callback",
    bucket: "negative",
    label: "No Callback / Delayed Response",
    description:
      "Retailer was unhappy because a promised callback, update or response did not happen within the expected timeframe.",
  },
  {
    id: "unhappy-repeated-follow-up",
    bucket: "negative",
    label: "Repeated Follow-up on the Same Request",
    description:
      "Retailer was unhappy because they had to contact customer service repeatedly about the same order, delay, pricing issue or unresolved request.",
  },
  {
    id: "unhappy-no-resolution",
    bucket: "negative",
    label: "No Clear Resolution / Next Action Provided",
    description:
      "Retailer was unhappy because, after contacting customer service, they still did not know what would happen next or who would follow up.",
  },
];

const TAXONOMY_BY_ID = Object.fromEntries(SENTIMENT_REASON_TAXONOMY.map((e) => [e.id, e]));

const normalizeReasonText = (reason) =>
  String(reason || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalizeLabelKey = (text) =>
  normalizeReasonText(text)
    .replace(/&/g, "and")
    .replace(/[^\w\s/-]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** Map row sentiment / voice key / display label → taxonomy bucket. */
export const sentimentToBucket = (sentiment) => {
  const s = normalizeReasonText(sentiment);
  if (!s || s === "n/a") return null;
  if (s === "negative" || s.includes("unhappy")) return "negative";
  if (s === "positive" || s.includes("happy")) return "positive";
  if (s === "neutral") return "neutral";
  return null;
};

/** Voice dashboard sentiment key (positive | neutral | negative). */
export const voiceRowSentimentBucket = (row) => {
  const fromField =
    sentimentToBucket(row?.sentiment) || sentimentToBucket(row?.Customer_Sentiment);
  return fromField || "neutral";
};

/** Digital row sentiment → bucket. */
export const digitalRowSentimentBucket = (row) => sentimentToBucket(row?.sentiment) || "neutral";

export const extractSentimentReasonRaw = (row) =>
  String(
    row?.sentiment_reason ??
      row?.sentimentReason ??
      row?.reason_for_sentiment ??
      row?.Sentiment_Reason ??
      row?.reason_sentiment ??
      row?.customer_sentiment_reason ??
      row?.retailer_sentiment_reason ??
      ""
  ).trim();

/**
 * Keyword rules (first match within bucket wins; unhappy checked before neutral when bucket unknown).
 * @type {{ id: string, re: RegExp }[]}
 */
const KEYWORD_RULES = [
  { id: "unhappy-no-resolution", re: /did not know what would happen|no clear resolution|next action provided/ },
  { id: "unhappy-no-callback", re: /promised callback|delayed response|did not happen within the expected/ },
  { id: "unhappy-repeated-follow-up", re: /repeatedly about the same|repeated follow-up/ },
  {
    id: "unhappy-incorrect-info",
    re: /information you gave me is not sufficient|not sufficient or correct|incorrect\/incomplete information|cannot move forward/,
  },
  {
    id: "unhappy-order-delay",
    re: /could not meet.*deadline|required deadline could not|significantly longer than expected|delayed beyond expected|project delayed|order \/ project delayed beyond expected date/,
  },
  { id: "neutral-order-status", re: /order status|locate an order|missing order confirmation|reorder/ },
  { id: "neutral-pricing-pending", re: /waiting for pricing|quote confirmation pending|pricing discrepancy|pricing correction/ },
  { id: "neutral-delivery-pending", re: /uncertain about.*(ship|delivery|lead time)|delivery\/ timing confirmation/ },
  { id: "neutral-product-clarify", re: /additional clarification|information clarification|before proceed/ },
  { id: "neutral-callback-pending", re: /waiting for another team|investigation.*pending|internal confirmation pending/ },
  { id: "happy-pricing", re: /pricing.*(provided|received the price)|quote provided|received the price, quote/ },
  { id: "happy-order-complete", re: /successfully processed|request successfully completed|order.*successfully/ },
  { id: "happy-ship-info", re: /ship date|delivery timing|tracking|rush-shipping|delivery information provided/ },
  { id: "happy-product-details", re: /information needed to proceed|product.*stock.*order details|details clarified/ },
  { id: "happy-follow-up", re: /clear follow-up|next-step plan|confidence about what would happen/ },
];

const matchKeyword = (norm, preferBucket, bucketOnly = false) => {
  const tryOrder = (bucket) => {
    for (const { id, re } of KEYWORD_RULES) {
      const entry = TAXONOMY_BY_ID[id];
      if (entry.bucket !== bucket) continue;
      if (re.test(norm)) return entry;
    }
    return null;
  };
  if (preferBucket) {
    const hit = tryOrder(preferBucket);
    if (hit) return hit;
    if (bucketOnly) return null;
  }
  for (const bucket of ["negative", "neutral", "positive"]) {
    const hit = tryOrder(bucket);
    if (hit) return hit;
  }
  return null;
};

const reasonTokens = (text) =>
  normalizeReasonText(text)
    .split(/\W+/)
    .filter((w) => w.length > 2);

const tokenOverlapScore = (a, b) => {
  const tb = new Set(reasonTokens(b));
  if (tb.size === 0) return 0;
  let score = 0;
  for (const t of reasonTokens(a)) {
    if (tb.has(t)) score += 1;
  }
  return score;
};

const bucketDefaultReason = (bucket) =>
  TAXONOMY_BY_ID[SENTIMENT_BUCKET_DEFAULT_REASON_ID[bucket]] || TAXONOMY_BY_ID["neutral-order-status"];

/** Closest of the five taxonomy entries in `bucket` (token overlap on label + description). */
const closestReasonInBucket = (norm, bucket) => {
  let best = null;
  let bestScore = 0;
  for (const entry of SENTIMENT_REASON_TAXONOMY) {
    if (entry.bucket !== bucket) continue;
    const score = Math.max(
      tokenOverlapScore(norm, entry.label) * 2,
      tokenOverlapScore(norm, entry.description)
    );
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }
  return bestScore > 0 ? best : bucketDefaultReason(bucket);
};

/**
 * @returns {{ id: string, bucket: SentimentBucket, label: string, description: string }}
 */
export const canonicalizeSentimentReason = (sentiment, rawReason) => {
  const preferBucket = sentimentToBucket(sentiment) || "neutral";
  const raw = String(rawReason ?? "").trim();
  if (!raw) return bucketDefaultReason(preferBucket);

  const norm = normalizeReasonText(raw);
  const labelKey = normalizeLabelKey(raw);

  if (
    labelKey.includes("required deadline could not be met") ||
    labelKey.includes("order project delayed beyond expected date") ||
    labelKey.includes("order delayed beyond expected date")
  ) {
    return TAXONOMY_BY_ID["unhappy-order-delay"];
  }
  if (
    labelKey.includes("incorrect incomplete information") ||
    norm.includes("information you gave me is not sufficient")
  ) {
    return TAXONOMY_BY_ID["unhappy-incorrect-info"];
  }

  for (const entry of SENTIMENT_REASON_TAXONOMY) {
    if (entry.bucket !== preferBucket) continue;
    if (normalizeLabelKey(entry.label) === labelKey) return entry;
  }
  for (const entry of SENTIMENT_REASON_TAXONOMY) {
    if (entry.bucket !== preferBucket) continue;
    if (normalizeReasonText(entry.description) === norm) return entry;
  }
  for (const entry of SENTIMENT_REASON_TAXONOMY) {
    if (entry.bucket !== preferBucket) continue;
    const desc = normalizeReasonText(entry.description);
    if (desc.startsWith(norm) || norm.startsWith(desc.slice(0, Math.min(desc.length, 72)))) return entry;
  }

  const keywordHit = matchKeyword(norm, preferBucket, true);
  if (keywordHit) return keywordHit;

  return closestReasonInBucket(norm, preferBucket);
};

export const canonicalSentimentReasonLabel = (sentiment, rawReason) => {
  const canon = canonicalizeSentimentReason(sentiment, rawReason);
  return canon?.label ?? "";
};

export const buildTopSentimentReasonBuckets = (rows, sentimentKey, topN = 5, getBucketFromRow) => {
  const freqById = new Map();
  (rows || []).forEach((row) => {
    if (getBucketFromRow(row) !== sentimentKey) return;
    const raw = extractSentimentReasonRaw(row);
    const sentimentForCanon = row?.sentiment ?? row?.Customer_Sentiment ?? sentimentKey;
    const canon = canonicalizeSentimentReason(sentimentForCanon, raw);
    const prev = freqById.get(canon.id);
    if (prev) prev.count += 1;
    else freqById.set(canon.id, { id: canon.id, label: canon.label, description: canon.description, count: 1 });
  });
  return [...freqById.values()]
    .filter((b) => b.count >= 1)
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
};

export const sentimentReasonMatchesFilter = (row, filter, sentimentFilter, getBucketFromRow) => {
  if (!filter || filter === "all") return true;
  if (sentimentFilter && sentimentFilter !== "all" && getBucketFromRow(row) !== sentimentFilter) {
    return false;
  }
  const raw = extractSentimentReasonRaw(row);
  const canon = canonicalizeSentimentReason(row?.sentiment, raw);
  const filterNorm = normalizeLabelKey(filter);
  if (normalizeLabelKey(canon.label) === filterNorm) return true;
  if (filter === "(blank)" && !raw) return true;
  return false;
};

export const buildSentimentReasonFilterOptions = (rows, sentimentFilter, getBucketFromRow) => {
  const pool =
    sentimentFilter && sentimentFilter !== "all"
      ? (rows || []).filter((row) => getBucketFromRow(row) === sentimentFilter)
      : rows || [];
  const freqById = new Map();
  pool.forEach((row) => {
    const raw = extractSentimentReasonRaw(row);
    const canon = canonicalizeSentimentReason(row?.sentiment, raw);
    if (!canon) return;
    const prev = freqById.get(canon.id);
    if (prev) prev.count += 1;
    else freqById.set(canon.id, { label: canon.label, count: 1 });
  });
  return [...freqById.values()].sort((a, b) => b.count - a.count).slice(0, 25);
};
